import React, { useEffect, useState } from "react";
import { Plus, Check, X, Trash2, GraduationCap, HeartPulse, Headphones, Package, Gavel, ClipboardList, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api, { formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Empty, Surface } from "@/components/app/Primitives";
import EmployeePicker from "@/components/app/EmployeePicker";
import CaseDocumentDialog, { AttendanceFacts, CATEGORY_LABEL } from "@/components/app/CaseDocumentDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/* ---------------- shared helpers ---------------- */
async function loadEmployees() {
  const { data } = await api.get("/hris/employees", { params: { limit: 500 } });
  const map = {};
  (data.items || []).forEach((e) => { map[e.id] = `${e.last_name}, ${e.first_name}`; });
  return { list: data.items || [], map };
}

// Searchable combobox (server-side search) — scales to thousands of employees.
// `employees` prop kept for backwards compatibility but no longer needed.
function EmployeeSelect({ value, onChange, employees, placeholder = "Select employee" }) {
  return <EmployeePicker value={value} onChange={onChange} placeholder={placeholder} />;
}

/* ---------------- Onboarding ---------------- */
export function OnboardingPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", probation_end_date: "" });
  const [taskOpen, setTaskOpen] = useState(null);
  const [newTask, setNewTask] = useState("");

  const load = async () => {
    const [{ data }, { list, map }] = await Promise.all([
      api.get("/onboarding"),
      loadEmployees(),
    ]);
    setItems(data.items || []);
    setEmployees(list);
    setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.employee_id) return toast.error("Employee required");
    try {
      const seed = ["Sign contract", "Submit BIR 2316", "Government IDs verified", "IT account setup", "Orientation completed"];
      const payload = {
        ...form,
        tasks: seed.map((name) => ({ name, completed: false })),
      };
      await api.post("/onboarding", payload);
      toast.success("Onboarding created");
      setOpen(false); setForm({ employee_id: "", probation_end_date: "" });
      load();
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  const toggle = async (ob, taskName) => {
    try {
      await api.post(`/onboarding/${ob.id}/tasks/${encodeURIComponent(taskName)}/toggle`, {});
      load();
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const addTask = async (ob) => {
    if (!newTask.trim()) return;
    try {
      await api.post(`/onboarding/${ob.id}/tasks`, { name: newTask });
      setNewTask("");
      load();
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Onboarding"
        subtitle="Probation tracking, requirement checklist, and onboarding completion."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="size-4 mr-1" /> New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Start onboarding</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee</Label>
                  <EmployeeSelect value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} employees={employees} />
                </div>
                <div><Label>Probation End</Label>
                  <Input type="date" value={form.probation_end_date} onChange={(e) => setForm({ ...form, probation_end_date: e.target.value })} />
                </div>
              </div>
              <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!items.length && <Empty title="No onboarding records" />}

      <div className="grid gap-3 lg:grid-cols-2">
        {items.map((ob) => {
          const completed = (ob.tasks || []).filter((t) => t.completed).length;
          const total = (ob.tasks || []).length;
          const pct = total ? Math.round((completed / total) * 100) : 0;
          return (
            <Card key={ob.id}>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{empMap[ob.employee_id] || ob.employee_id.slice(0, 8)}</span>
                  <Badge variant="outline" className="capitalize">{ob.status?.replaceAll("_"," ")}</Badge>
                </CardTitle>
                <div className="text-xs text-muted-foreground">
                  Started {formatDate(ob.started_at)} · Probation ends {formatDate(ob.probation_end_date)} · {pct}% complete
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(ob.tasks || []).map((t, i) => (
                    <label key={i} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!t.completed} onChange={() => toggle(ob, t.id || t.name)} />
                      <span className={t.completed ? "line-through text-muted-foreground" : ""}>{t.name}</span>
                      {t.due_date && <span className="text-xs text-muted-foreground ml-auto">{formatDate(t.due_date)}</span>}
                    </label>
                  ))}
                  {!ob.tasks?.length && <div className="text-xs text-muted-foreground">No tasks yet.</div>}
                </div>
                <div className="mt-3 flex gap-2">
                  <Input placeholder="New task…" value={taskOpen === ob.id ? newTask : ""}
                    onFocus={() => setTaskOpen(ob.id)}
                    onChange={(e) => setNewTask(e.target.value)} />
                  <Button size="sm" variant="outline" onClick={() => addTask(ob)}>Add</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Performance ---------------- */
export function PerformancePage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", cycle: "" });
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const [{ data }, { list, map }] = await Promise.all([api.get("/performance"), loadEmployees()]);
    setItems(data.items || []); setEmployees(list); setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/performance", form); toast.success("Review created"); setOpen(false); setForm({ employee_id: "", cycle: "" }); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const rate = async (pr) => {
    try {
      await api.post(`/performance/${pr.id}/rate`, {
        manager_rating: Number(edit.manager_rating ?? pr.manager_rating ?? 0),
        self_rating: Number(edit.self_rating ?? pr.self_rating ?? 0),
        final_rating: Number(edit.final_rating ?? pr.final_rating ?? 0),
        comments: edit.comments ?? pr.comments ?? "",
      });
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const submit = async (pr) => {
    try { await api.post(`/performance/${pr.id}/submit`, { status: "submitted" }); toast.success("Submitted"); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Performance"
        subtitle="Review cycles, manager + self ratings, final scoring, and submission lifecycle."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="size-4 mr-1" /> New Cycle</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New performance review</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee</Label>
                  <EmployeeSelect value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} employees={employees} />
                </div>
                <div><Label>Cycle (e.g. 2026-Q1)</Label><Input value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!items.length && <Empty title="No reviews yet" />}

      <div className="grid gap-3 lg:grid-cols-2">
        {items.map((pr) => (
          <Card key={pr.id}>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>{empMap[pr.employee_id] || pr.employee_id?.slice(0, 8)}</span>
                <Badge variant="outline" className="capitalize">{pr.status}</Badge>
              </CardTitle>
              <div className="text-xs text-muted-foreground">Cycle: {pr.cycle}</div>
            </CardHeader>
            <CardContent>
              {edit?.id === pr.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Manager</Label><Input type="number" step="0.1" defaultValue={pr.manager_rating || ""}
                      onChange={(e) => setEdit({ ...edit, manager_rating: e.target.value })} /></div>
                    <div><Label className="text-xs">Self</Label><Input type="number" step="0.1" defaultValue={pr.self_rating || ""}
                      onChange={(e) => setEdit({ ...edit, self_rating: e.target.value })} /></div>
                    <div><Label className="text-xs">Final</Label><Input type="number" step="0.1" defaultValue={pr.final_rating || ""}
                      onChange={(e) => setEdit({ ...edit, final_rating: e.target.value })} /></div>
                  </div>
                  <div><Label className="text-xs">Comments</Label><Textarea defaultValue={pr.comments || ""}
                    onChange={(e) => setEdit({ ...edit, comments: e.target.value })} /></div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => rate(pr)}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div>Manager: <b>{pr.manager_rating ?? "—"}</b></div>
                  <div>Self: <b>{pr.self_rating ?? "—"}</b></div>
                  <div>Final: <b>{pr.final_rating ?? "—"}</b></div>
                  {pr.comments && <div className="text-xs text-muted-foreground">{pr.comments}</div>}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEdit({ id: pr.id })}>Rate</Button>
                    {pr.status !== "submitted" && <Button size="sm" onClick={() => submit(pr)}>Submit</Button>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Training ---------------- */
export function TrainingPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", schedule_date: "", location: "" });
  const [enrollFor, setEnrollFor] = useState(null);
  const [completeFor, setCompleteFor] = useState(null);

  const load = async () => {
    const [{ data }, { list, map }] = await Promise.all([api.get("/training"), loadEmployees()]);
    setItems(data.items || []); setEmployees(list); setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/training", form); toast.success("Created"); setOpen(false); setForm({ title: "", schedule_date: "", location: "" }); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const enroll = async (t, eid) => {
    if (!eid) return;
    try { await api.post(`/training/${t.id}/enroll`, { employee_id: eid }); toast.success("Enrolled"); setEnrollFor(null); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const complete = async (t, body) => {
    try { await api.post(`/training/${t.id}/complete`, body); toast.success("Marked complete"); setCompleteFor(null); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Training"
        subtitle="Calendar, enrollment, completion + certificate tracking."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="size-4 mr-1" /> New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New training</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Schedule Date</Label><Input type="date" value={form.schedule_date} onChange={(e) => setForm({ ...form, schedule_date: e.target.value })} /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!items.length && <Empty title="No training scheduled" />}

      <div className="grid gap-3 lg:grid-cols-2">
        {items.map((t) => (
          <Card key={t.id}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="size-4" /> {t.title}</CardTitle>
              <div className="text-xs text-muted-foreground">
                {formatDate(t.schedule_date)} · {t.location || "—"} · {(t.attendees || []).length} attendee(s)
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-sm space-y-1">
                {(t.attendees || []).map((eid) => {
                  const c = (t.completion_map || {})[eid] || {};
                  return (
                    <div key={eid} className="flex items-center justify-between gap-2 border rounded p-2">
                      <div>
                        <div className="font-medium">{empMap[eid] || eid.slice(0,8)}</div>
                        <div className="text-xs text-muted-foreground">{c.status || "enrolled"}{c.completed_at ? ` · ${formatDate(c.completed_at)}` : ""}{c.score != null ? ` · score ${c.score}` : ""}</div>
                      </div>
                      {c.status !== "completed" && (
                        <Button size="sm" variant="outline"
                          onClick={() => {
                            const score = window.prompt("Score (optional):");
                            complete(t, { employee_id: eid, score: score ? Number(score) : undefined });
                          }}>Mark Complete</Button>
                      )}
                    </div>
                  );
                })}
                {!t.attendees?.length && <div className="text-xs text-muted-foreground">No attendees yet.</div>}
              </div>
              <div className="mt-3 flex gap-2 items-center">
                <EmployeeSelect value={enrollFor?.[t.id] || ""} onChange={(v) => setEnrollFor({ ...(enrollFor || {}), [t.id]: v })} employees={employees} placeholder="Enroll employee" />
                <Button size="sm" onClick={() => enroll(t, (enrollFor || {})[t.id])}>Enroll</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Relations / Disciplinary ---------------- */
export function RelationsPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", case_type: "incident", severity: "low", description: "", category: "", incident_dates: "" });
  const [docFor, setDocFor] = useState(null);
  const [actionFor, setActionFor] = useState(null);
  const [act, setAct] = useState({ type: "note", description: "" });

  const load = async () => {
    const [{ data }, { list, map }] = await Promise.all([api.get("/relations"), loadEmployees()]);
    setItems(data.items || []); setEmployees(list); setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const body = { ...form }; if (!body.category) delete body.category; if (!body.incident_dates) delete body.incident_dates;
      await api.post("/relations", body); toast.success("Case opened"); setOpen(false);
      setForm({ employee_id: "", case_type: "incident", severity: "low", description: "", category: "", incident_dates: "" }); load();
    }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const addAction = async (c) => {
    if (!act.description.trim()) return toast.error("Description required");
    try { await api.post(`/relations/${c.id}/actions`, act); toast.success("Action logged"); setActionFor(null); setAct({ type: "note", description: "" }); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Employee Relations" subtitle="Incidents, disciplinary actions, and case logs with audit trail."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="size-4 mr-1" /> New Case</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New case</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee</Label><EmployeeSelect value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} employees={employees} /></div>
                <div><Label>Type</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.case_type} onChange={(e) => setForm({ ...form, case_type: e.target.value })}>
                  <option>incident</option><option>disciplinary</option><option>grievance</option>
                </select></div>
                <div><Label>Severity</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                  <option>low</option><option>medium</option><option>high</option>
                </select></div>
                <div><Label>Category</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, case_type: e.target.value ? "disciplinary" : form.case_type })} data-testid="case-category">
                  <option value="">None</option><option value="tardiness">Tardiness</option><option value="undertime">Undertime</option><option value="awol">AWOL (absence without official leave)</option>
                </select></div>
                <AttendanceFacts employeeId={form.employee_id} category={form.category}
                  onFacts={(txt, meta) => setForm((f) => ({ ...f, description: txt || f.description, incident_dates: meta?.start ? `${meta.start} to ${meta.end}` : f.incident_dates }))} />
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="case-description" /></div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="case-open">Open Case</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!items.length && <Empty title="No cases logged" />}

      <div className="space-y-3">
        {items.map((c) => (
          <Card key={c.id}>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span><Gavel className="inline size-4 mr-1" /> {empMap[c.employee_id] || c.employee_id?.slice(0,8)} — {c.case_type}{c.category && <Badge variant="outline" className="ml-2 align-middle" data-testid={`case-cat-${c.id}`}>{CATEGORY_LABEL[c.category] || c.category}</Badge>}</span>
                <Badge variant={c.status === "closed" ? "outline" : "default"} className="capitalize">{c.status}</Badge>
              </CardTitle>
              <div className="text-xs text-muted-foreground capitalize">Severity: {c.severity}</div>
            </CardHeader>
            <CardContent>
              <div className="text-sm whitespace-pre-wrap mb-2">{c.description}</div>
              <div className="text-xs font-medium mb-1">Action log</div>
              <div className="space-y-1">
                {(c.actions || []).map((a) => (
                  <div key={a.id} className="text-xs border rounded p-2">
                    <b className="capitalize">{a.type}</b> · {formatDate(a.at)} · {a.by_email || ""}
                    <div>{a.description}</div>
                  </div>
                ))}
                {!c.actions?.length && <div className="text-xs text-muted-foreground">No actions yet.</div>}
              </div>
              {actionFor === c.id ? (
                <div className="mt-3 space-y-2">
                  <select className="w-full h-9 px-2 rounded-md border border-border text-sm" value={act.type} onChange={(e) => setAct({ ...act, type: e.target.value })}>
                    <option>note</option><option>notice</option><option>warning</option><option>suspension</option><option>termination</option>
                  </select>
                  <Textarea placeholder="Description" value={act.description} onChange={(e) => setAct({ ...act, description: e.target.value })} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => addAction(c)}>Log Action</Button>
                    <Button size="sm" variant="outline" onClick={() => { setActionFor(null); setAct({ type: "note", description: "" }); }}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setActionFor(c.id)}>Add Action</Button>
                  <Button size="sm" onClick={() => setDocFor(c)} data-testid={`prepare-doc-${c.id}`}>Prepare document</Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <CaseDocumentDialog open={!!docFor} onClose={() => setDocFor(null)} kase={docFor}
        employeeName={docFor ? (empMap[docFor.employee_id] || "the employee") : ""} onIssued={load} />
    </div>
  );
}

/* ---------------- Helpdesk ---------------- */
export function HelpdeskPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "hr", priority: "normal", description: "" });

  const load = async () => {
    const [{ data }, { list, map }] = await Promise.all([api.get("/helpdesk"), loadEmployees()]);
    setItems(data.items || []); setEmployees(list); setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/helpdesk", form); toast.success("Ticket created"); setOpen(false); setForm({ subject: "", category: "hr", priority: "normal", description: "" }); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (t, status) => {
    try {
      const body = { status };
      if (status === "resolved") body.resolution_notes = window.prompt("Resolution notes:") || "";
      await api.post(`/helpdesk/${t.id}/status`, body);
      load();
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const comment = async (t) => {
    const body = window.prompt("Comment:");
    if (!body) return;
    try { await api.post(`/helpdesk/${t.id}/comment`, { body }); load(); } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Helpdesk" subtitle="HR / Payroll / Benefits tickets with status lifecycle and audit trail."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="size-4 mr-1" /> New Ticket</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New ticket</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                <div><Label>Category</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option>hr</option><option>payroll</option><option>benefits</option>
                </select></div>
                <div><Label>Priority</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option>low</option><option>normal</option><option>high</option><option>urgent</option>
                </select></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create}>Submit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!items.length && <Empty title="No tickets" />}

      <div className="space-y-3">
        {items.map((t) => (
          <Card key={t.id} data-row-id={t.id}>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span><Headphones className="inline size-4 mr-1" /> {t.subject}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">{t.priority}</Badge>
                  <Badge variant="outline" className="capitalize">{t.status}</Badge>
                </div>
              </CardTitle>
              <div className="text-xs text-muted-foreground capitalize">
                {t.category} · {empMap[t.employee_id] || "—"} · {formatDate(t.created_at)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-sm whitespace-pre-wrap">{t.description}</div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setStatus(t, "in_progress")} disabled={t.status === "in_progress"}>Start</Button>
                <Button size="sm" onClick={() => setStatus(t, "resolved")} disabled={t.status === "resolved" || t.status === "closed"}>Resolve</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(t, "closed")} disabled={t.status === "closed"}>Close</Button>
                <Button size="sm" variant="outline" onClick={() => comment(t)}>Comment</Button>
              </div>
              {!!(t.comments || []).length && (
                <div className="mt-3 space-y-1">
                  <div className="text-xs font-medium">Comments</div>
                  {t.comments.map((c) => (
                    <div key={c.id} className="text-xs border rounded p-2">
                      <div className="text-muted-foreground">{c.by_email} · {formatDate(c.at)}</div>
                      <div>{c.body}</div>
                    </div>
                  ))}
                </div>
              )}
              {t.resolution_notes && (
                <div className="mt-3 text-xs text-muted-foreground">Resolution: {t.resolution_notes}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Benefits ---------------- */
export function BenefitsPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", benefit_type: "hmo", provider: "" });
  const [enrollFor, setEnrollFor] = useState({});

  const load = async () => {
    const [{ data }, { list, map }] = await Promise.all([api.get("/benefits"), loadEmployees()]);
    setItems(data.items || []); setEmployees(list); setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/benefits", form); toast.success("Created"); setOpen(false); setForm({ name: "", benefit_type: "hmo", provider: "" }); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const enroll = async (b, eid) => {
    if (!eid) return;
    try { await api.post(`/benefits/${b.id}/enroll`, { employee_id: eid }); toast.success("Enrolled"); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const unenroll = async (b, eid) => {
    try { await api.post(`/benefits/${b.id}/unenroll`, { employee_id: eid }); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Benefits" subtitle="HMO, allowances, and benefits enrollment lifecycle."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="size-4 mr-1" /> New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New benefit plan</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Type</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.benefit_type} onChange={(e) => setForm({ ...form, benefit_type: e.target.value })}>
                  <option>hmo</option><option>allowance</option><option>insurance</option>
                </select></div>
                <div><Label>Provider</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!items.length && <Empty title="No benefits configured" />}

      <div className="grid gap-3 lg:grid-cols-2">
        {items.map((b) => (
          <Card key={b.id}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><HeartPulse className="size-4" /> {b.name}</CardTitle>
              <div className="text-xs text-muted-foreground capitalize">{b.benefit_type} · {b.provider || "—"} · {(b.enrolled||[]).length} enrolled</div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {(b.enrolled || []).map((eid) => (
                  <div key={eid} className="flex items-center justify-between text-sm border rounded p-2">
                    <span>{empMap[eid] || eid.slice(0,8)}</span>
                    <Button size="sm" variant="outline" onClick={() => unenroll(b, eid)}>Remove</Button>
                  </div>
                ))}
                {!b.enrolled?.length && <div className="text-xs text-muted-foreground">No enrollees.</div>}
              </div>
              <div className="mt-3 flex gap-2 items-center">
                <EmployeeSelect value={enrollFor[b.id] || ""} onChange={(v) => setEnrollFor({ ...enrollFor, [b.id]: v })} employees={employees} placeholder="Enroll employee" />
                <Button size="sm" onClick={() => enroll(b, enrollFor[b.id])}>Enroll</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Assets ---------------- */
export function AssetsPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "laptop", serial_no: "" });
  const [assignFor, setAssignFor] = useState({});

  const load = async () => {
    const [{ data }, { list, map }] = await Promise.all([api.get("/assets"), loadEmployees()]);
    setItems(data.items || []); setEmployees(list); setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/assets", form); toast.success("Created"); setOpen(false); setForm({ name: "", category: "laptop", serial_no: "" }); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const assign = async (a, eid) => {
    if (!eid) return;
    try { await api.post(`/assets/${a.id}/assign`, { employee_id: eid }); toast.success("Assigned"); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };
  const returnAsset = async (a) => {
    const condition = window.prompt("Condition (good/damaged/lost):", "good") || "good";
    const status = condition === "lost" ? "lost" : (condition === "damaged" ? "damaged" : "available");
    try { await api.post(`/assets/${a.id}/return`, { condition, status }); toast.success("Returned"); load(); }
    catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Assets" subtitle="Asset assignment, returns, history, and clearance linkage."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="size-4 mr-1" /> New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New asset</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Category</Label><select className="w-full h-10 px-3 rounded-md border border-border" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option>laptop</option><option>phone</option><option>monitor</option><option>vehicle</option><option>other</option>
                </select></div>
                <div><Label>Serial</Label><Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!items.length && <Empty title="No assets" />}

      <div className="overflow-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="p-2">Name</th>
              <th className="p-2">Category</th>
              <th className="p-2">Serial</th>
              <th className="p-2">Status</th>
              <th className="p-2">Assigned To</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t">
                <td className="p-2 font-medium"><Package className="inline size-3 mr-1" /> {a.name}</td>
                <td className="p-2 capitalize">{a.category}</td>
                <td className="p-2 text-xs">{a.serial_no || "—"}</td>
                <td className="p-2"><Badge variant="outline" className="capitalize">{a.status}</Badge></td>
                <td className="p-2 text-xs">{a.assigned_to ? (empMap[a.assigned_to] || a.assigned_to.slice(0,8)) : "—"}</td>
                <td className="p-2">
                  {a.status === "assigned" ? (
                    <Button size="sm" variant="outline" onClick={() => returnAsset(a)}>Return</Button>
                  ) : (
                    <div className="flex gap-1 items-center">
                      <EmployeeSelect value={assignFor[a.id] || ""} onChange={(v) => setAssignFor({ ...assignFor, [a.id]: v })} employees={employees} placeholder="Assign" />
                      <Button size="sm" onClick={() => assign(a, assignFor[a.id])}>Assign</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
